import { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { 
  Download as DownloadIcon, 
  Lock, 
  File, 
  FileText, 
  FileImage, 
  FileVideo, 
  FileArchive,
  FileSpreadsheet,
  Eye,
  EyeOff,
  Loader2,
  AlertCircle,
  CheckCircle
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

const API = process.env.REACT_APP_BACKEND_URL ? `${process.env.REACT_APP_BACKEND_URL}/api` : '/api';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_filelink-pro/artifacts/2x5fb85x_logo-avec-legende-et-Bakgroung-d%C3%A9taill%C3%A9.png";

// File type detection (same as Dashboard)
const getFileIcon = (filename, type) => {
  const ext = filename?.toLowerCase().split('.').pop() || '';
  const mimeType = type?.toLowerCase() || '';
  
  if (['pdf'].includes(ext) || mimeType.includes('pdf')) {
    return <FileText className="w-5 h-5" />;
  }
  if (['doc', 'docx', 'txt', 'rtf', 'odt'].includes(ext)) {
    return <FileText className="w-5 h-5" />;
  }
  if (['xls', 'xlsx', 'csv', 'ods'].includes(ext)) {
    return <FileSpreadsheet className="w-5 h-5" />;
  }
  if (['jpg', 'jpeg', 'png', 'gif', 'webp', 'svg'].includes(ext) || mimeType.includes('image')) {
    return <FileImage className="w-5 h-5" />;
  }
  if (['mp4', 'mov', 'avi', 'mkv', 'webm'].includes(ext) || mimeType.includes('video')) {
    return <FileVideo className="w-5 h-5" />;
  }
  if (['zip', 'rar', '7z', 'tar', 'gz'].includes(ext)) {
    return <FileArchive className="w-5 h-5" />;
  }
  return <File className="w-5 h-5" />;
};

const formatBytes = (bytes) => {
  if (bytes === 0) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB', 'TB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
};

export default function Download() {
  const { linkId } = useParams();
  const [linkInfo, setLinkInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [passwordVerified, setPasswordVerified] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [verifying, setVerifying] = useState(false);

  useEffect(() => {
    const fetchLinkInfo = async () => {
      try {
        const res = await axios.get(`${API}/links/${linkId}`);
        setLinkInfo(res.data);
        
        // If no password required, auto-verify
        if (!res.data.has_password) {
          setPasswordVerified(true);
        }
      } catch (err) {
        console.error("Error fetching link:", err);
        if (err.response?.status === 404) {
          setError("Ce lien n'existe pas ou a expire");
        } else {
          setError("Erreur lors du chargement du lien");
        }
      } finally {
        setLoading(false);
      }
    };

    fetchLinkInfo();
  }, [linkId]);

  const verifyPassword = async () => {
    if (!password.trim()) {
      toast.error("Veuillez entrer un mot de passe");
      return;
    }

    setVerifying(true);
    try {
      await axios.post(`${API}/links/${linkId}/verify`, { password });
      setPasswordVerified(true);
      toast.success("Mot de passe correct");
    } catch (err) {
      toast.error("Mot de passe incorrect");
    } finally {
      setVerifying(false);
    }
  };

  const handleDownload = async () => {
    setDownloading(true);
    try {
      const params = linkInfo.has_password ? { password } : {};
      const res = await axios.get(`${API}/download/${linkId}`, { params });
      
      // If redirect_url is provided (single file), open it
      if (res.data.redirect_url) {
        window.open(res.data.redirect_url, '_blank');
      } else {
        // For ZIP response, trigger download
        const blob = new Blob([res.data], { type: 'application/zip' });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `nerium_files_${linkId.slice(0, 8)}.zip`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
      }
      
      toast.success("Telechargement lance");
    } catch (err) {
      console.error("Download error:", err);
      toast.error("Erreur lors du telechargement");
    } finally {
      setDownloading(false);
    }
  };

  // Handle Enter key for password
  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !verifying) {
      verifyPassword();
    }
  };

  if (loading) {
    return (
      <div className="download-container" data-testid="download-page-loading">
        <Loader2 className="w-12 h-12 animate-spin text-[#3B4394]" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="download-container" data-testid="download-page-error">
        <div className="download-card">
          <AlertCircle className="w-16 h-16 text-[#C3171A] mx-auto mb-4" />
          <h1 className="nerium-h2 mb-2">Lien invalide</h1>
          <p className="text-[#5C6673]">{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="download-container" data-testid="download-page">
      <div className="download-card">
        {/* Logo */}
        <div className="mb-8">
          <img src={LOGO_URL} alt="NERIUM" className="h-16 mx-auto" />
        </div>

        {/* Title */}
        <h1 className="nerium-h2 mb-2">Telechargement de fichiers</h1>
        <p className="text-[#5C6673] mb-6">
          {linkInfo?.files_count} fichier(s) disponible(s)
        </p>

        {/* Password Form */}
        {linkInfo?.has_password && !passwordVerified && (
          <div className="mb-6" data-testid="password-form">
            <div className="flex items-center justify-center gap-2 mb-4 text-[#5C6673]">
              <Lock className="w-5 h-5" />
              <span>Ce lien est protege par un mot de passe</span>
            </div>
            
            <div className="relative mb-4">
              <Input
                type={showPassword ? "text" : "password"}
                placeholder="Entrez le mot de passe"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                onKeyPress={handleKeyPress}
                className="nerium-input pr-10"
                data-testid="download-password-input"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="password-toggle"
              >
                {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
            
            <Button
              onClick={verifyPassword}
              disabled={verifying}
              className="nerium-btn-primary w-full"
              data-testid="verify-password-button"
            >
              {verifying ? (
                <Loader2 className="w-4 h-4 animate-spin mr-2" />
              ) : (
                <Lock className="w-4 h-4 mr-2" />
              )}
              Verifier
            </Button>
          </div>
        )}

        {/* Files List & Download Button */}
        {passwordVerified && (
          <>
            {/* Files Preview */}
            <div className="mb-6 text-left" data-testid="files-preview">
              <p className="nerium-label mb-3">Fichiers inclus :</p>
              <div className="space-y-2 max-h-48 overflow-y-auto">
                {linkInfo?.files?.map((file, index) => (
                  <div 
                    key={file.id || index} 
                    className="flex items-center gap-3 p-2 bg-[#F9F9FA] rounded-sm"
                    data-testid={`preview-file-${index}`}
                  >
                    <div className="text-[#3B4394]">
                      {getFileIcon(file.filename, file.type)}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm text-[#0A0A0A] truncate">{file.filename}</p>
                      <p className="text-xs text-[#5C6673]">{formatBytes(file.size)}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Download Button */}
            <Button
              onClick={handleDownload}
              disabled={downloading}
              className="nerium-btn-primary w-full py-3"
              data-testid="download-button"
            >
              {downloading ? (
                <Loader2 className="w-5 h-5 animate-spin mr-2" />
              ) : (
                <DownloadIcon className="w-5 h-5 mr-2" />
              )}
              Telecharger {linkInfo?.files_count > 1 ? '(ZIP)' : ''}
            </Button>

            {/* Success indicator */}
            <div className="flex items-center justify-center gap-2 mt-4 text-[#166534]">
              <CheckCircle className="w-4 h-4" />
              <span className="text-sm">Pret pour le telechargement</span>
            </div>
          </>
        )}

        {/* Download count */}
        {linkInfo?.download_count > 0 && (
          <p className="text-xs text-[#5C6673] mt-6">
            Telecharge {linkInfo.download_count} fois
          </p>
        )}
      </div>

      {/* Footer */}
      <p className="text-xs text-[#5C6673] mt-8">
        Partage de fichiers securise par NERIUM
      </p>
    </div>
  );
}
